import { useMemo } from "react";
import { useAppSelector } from "@/store/hooks";
import { resolveReservationStart } from "@/lib/calendar";
import { parseStoredOrder, type V2StoredOrder } from "@/types/preorderV2";
import { zonedDateParts, type ZonedDateParts } from "@/v2/format";
import type { ReservationState } from "@/features/reservation/reservationSlice";
import { isEditLocked, lockMessageFor } from "@/utils/editLock";

/**
 * Whether the pre-order is closed to edits: the server's `editLock` says so
 * (the clock, or the restaurant sent it to the POS - FOYR-432), or the local
 * clock has passed the cutoff (order.lockAt, else the reservation time). The
 * one rule for the routes in App and for every V2 screen.
 */
export function isReservationLocked(
  reservation: Pick<ReservationState, "lockAtMs" | "expiresAtMs" | "editLock">,
  now: number = Date.now(),
): boolean {
  return isEditLocked(
    {
      expiresAtMs: reservation.lockAtMs ?? reservation.expiresAtMs,
      editLock: reservation.editLock,
    },
    now,
  );
}

export interface ReservationV2 {
  readonly reservationId?: string;
  readonly restaurantName: string;
  readonly address: string;
  readonly guestName: string;
  /** First whitespace-separated token of guestName ("Maya"). */
  readonly firstName: string;
  readonly partySize: number;
  readonly timeZone?: string;
  /** The reservation instant, or null when it can't be resolved. */
  readonly start: Date | null;
  readonly startParts: ZonedDateParts | null;
  /** The pre-order cutoff (order.lockAt, else the reservation time). */
  readonly lockParts: ZonedDateParts | null;
  /** Closed to edits (see isReservationLocked): the ticket is read-only. */
  readonly isLocked: boolean;
  /**
   * Why it is closed, in the guest's words (FOYR-432), or null when the app
   * can't say: a lock only the local clock knows about stays silent.
   */
  readonly lockMessage: string | null;
  readonly storedOrder: V2StoredOrder | null;
  readonly phoneLast4?: string;
}

/** The reservation fields the V2 screens read, typed and pre-formatted. */
export function useReservationV2(): ReservationV2 {
  const reservation = useAppSelector((s) => s.reservation);
  const {
    reservationId,
    restaurantName,
    address,
    guestName,
    partySize,
    restaurantTimeZone,
    reservationAtIso,
    reservationDate,
    reservationTime,
    lockAtMs,
    expiresAtMs,
    editLock,
    order,
    phoneLast4,
  } = reservation;

  const cutoffMs = lockAtMs ?? expiresAtMs;
  // Read every render, not memoised: the countdown re-renders this screen
  // as the cutoff approaches, and the ticket must lock the moment it passes.
  const now = Date.now();
  const isLocked = isReservationLocked({ lockAtMs, expiresAtMs, editLock }, now);
  const lockMessage = lockMessageFor({ editLock, lockAtMs, reservationAtIso }, now);

  const derived = useMemo(() => {
    const start = resolveReservationStart(
      reservationAtIso,
      reservationDate,
      reservationTime,
    );
    return {
      reservationId,
      restaurantName: restaurantName ?? "",
      address: address ?? "",
      guestName: (guestName ?? "").trim(),
      firstName: (guestName ?? "").trim().split(/\s+/)[0] ?? "",
      partySize,
      timeZone: restaurantTimeZone,
      start,
      startParts: zonedDateParts(start, restaurantTimeZone),
      lockParts: zonedDateParts(cutoffMs, restaurantTimeZone),
      storedOrder: parseStoredOrder(order),
      phoneLast4,
    };
  }, [
    reservationId,
    restaurantName,
    address,
    guestName,
    partySize,
    restaurantTimeZone,
    reservationAtIso,
    reservationDate,
    reservationTime,
    cutoffMs,
    order,
    phoneLast4,
  ]);

  return { ...derived, isLocked, lockMessage };
}
